/**
 * Feature E07-F03 — pure (DOM-free) model for the Dictionaries Settings card +
 * the Intake autocomplete wiring. Ported 1:1 from the prototype
 * `../EER app/prototype/assets/research.proto.js`:
 *   - `dictCol(title,key)`   → column metadata + counter (items.length);
 *   - `addDictTerm(e)`       → Enter-commit, trim, skip blank, no duplicate;
 *   - `removeChip(this)`     → remove a term from the column.
 *
 * These are framework-free decision helpers so they can be unit-tested without
 * mounting React (docs/architecture.md §12: services/logic unit tests, NOT
 * HTTP/e2e — routes are wired later). The React card (`DictionariesCard.tsx`) and
 * the Intake autocomplete (`research/IntakeAutocomplete.tsx`) read these.
 *
 * The wire shapes (`DictKey`, `DictionariesSnapshot`, the save union) and the
 * column metadata (`DICT_COLUMNS` — title + Intake `<datalist id>`) come from
 * `@eer/shared` (research-dictionaries.ts); this file adds only the small UI-side
 * pure rules on top of that contract.
 */
import {
  DICT_COLUMNS,
  type DictKey,
  type DictionariesSnapshot,
  type SaveDictionaryInput,
} from '@eer/shared';

/** Re-export the column metadata so the card/intake import one place. */
export { DICT_COLUMNS };
export type { DictKey, DictionariesSnapshot };

/** A keyboard event narrowed to just what the Enter-commit rule needs. */
export interface KeyIntentEvent {
  readonly key: string;
}

/**
 * Should an "Add term" keypress commit? Parity with prototype `addDictTerm`:
 * only the Enter key adds a term (everything else is ignored — normal typing).
 */
export function isAddTermKey(event: KeyIntentEvent): boolean {
  return event.key === 'Enter';
}

/**
 * Normalise a raw term the user typed: trim. The contract's `dictValueSchema`
 * also trims server-side, but doing it here keeps the optimistic UI honest and
 * lets `shouldAddTerm` reason about the cleaned value.
 */
export function normalizeTerm(raw: string): string {
  return raw.trim();
}

/**
 * Decide whether a (normalised) term is addable to a column's current values.
 * Parity with `addDictTerm`: a blank value is a no-op; a duplicate (case-sensitive,
 * matching the server's UNIQUE(dict_key,value)) is a no-op. Returns the
 * normalised term to add, or `null` when nothing should happen.
 */
export function termToAdd(rawValue: string, current: readonly string[]): string | null {
  const value = normalizeTerm(rawValue);
  if (value.length === 0) return null;
  if (current.includes(value)) return null;
  return value;
}

/** Build the `add` save payload for a term under a key (after `termToAdd`). */
export function buildAddInput(dict_key: DictKey, value: string): SaveDictionaryInput {
  return { action: 'add', dict_key, value };
}

/** Build the `remove` (soft-deactivate) save payload for a term under a key. */
export function buildRemoveInput(dict_key: DictKey, value: string): SaveDictionaryInput {
  return { action: 'remove', dict_key, value };
}

/**
 * The count badge shown in each column header (prototype `${items.length}`).
 * Trivial, but pinned by a test so the badge can never silently drift from the
 * rendered chip count.
 */
export function termCount(current: readonly string[]): number {
  return current.length;
}

/**
 * Read one column's active values out of the snapshot. The snapshot always has
 * every key present (the server guarantees an empty array, never a missing key),
 * so this never returns undefined.
 */
export function valuesForKey(
  snapshot: DictionariesSnapshot | undefined,
  key: DictKey,
): readonly string[] {
  if (!snapshot) return [];
  return snapshot[key];
}

// ---------------------------------------------------------------------------
// Intake autocomplete wiring (T04)
// ---------------------------------------------------------------------------

/** The `<datalist id>` that feeds a given dictionary key's autocomplete. */
export function datalistIdForKey(key: DictKey): string {
  const col = DICT_COLUMNS.find((c) => c.key === key);
  // DICT_COLUMNS covers all three keys, so this is always defined; the fallback
  // keeps the function total for the type checker.
  return col?.datalistId ?? '';
}

/**
 * Decide whether an Intake chip-field keypress commits the current value. Parity
 * with the prototype `chipKey(event, …)`: Enter adds a chip; everything else
 * (including picking a `<datalist>` suggestion, which fires `input`/`change`, not
 * a key-commit) is normal typing — so the autocomplete never blocks free manual
 * input of a value that is NOT in the dictionary (T04 acceptance #4).
 */
export function isChipCommitKey(event: KeyIntentEvent): boolean {
  return event.key === 'Enter';
}

/**
 * Decide whether a chip value is addable to the chip field's current values.
 * Same rule as the dictionary card: trim, skip blank, skip duplicate. The value
 * may be ANY string (a dictionary suggestion OR a freely typed one) — autocomplete
 * is a hint, not a constraint (T04 acceptance #4).
 */
export function chipToAdd(rawValue: string, current: readonly string[]): string | null {
  return termToAdd(rawValue, current);
}
